import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { fulfillmentsApi } from '../../lib/api';
import Modal from '../Modal';

interface OwnerConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmed?: () => void;
  fulfillmentId: string;
  rentalId?: string;
  itemTitle: string;
  renterName: string;
  pickupAddress: string;
  initialPickupDate?: string;
}

export default function OwnerConfirmationModal({
  isOpen,
  onClose,
  onConfirmed,
  fulfillmentId,
  rentalId,
  itemTitle,
  renterName,
  pickupAddress,
  initialPickupDate,
}: OwnerConfirmationModalProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Default to tomorrow at 2 PM if no initial date
  const defaultDate = initialPickupDate 
    ? new Date(initialPickupDate).toISOString().split('T')[0]
    : new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  
  const [pickupDate, setPickupDate] = useState(defaultDate);
  const [startTime, setStartTime] = useState('14:00');
  const [endTime, setEndTime] = useState('16:00');
  const [geofenceRadius, setGeofenceRadius] = useState(100);
  const [instructions, setInstructions] = useState('');

  const handleConfirm = async () => {
    setLoading(true);
    setError('');

    try {
      const pickupWindowStart = new Date(`${pickupDate}T${startTime}`);
      const pickupWindowEnd = new Date(`${pickupDate}T${endTime}`);

      // Use new confirm endpoint instead of updateStatus
      await fulfillmentsApi.confirmPickup(fulfillmentId, {
        pickupWindowStart: pickupWindowStart.toISOString(),
        pickupWindowEnd: pickupWindowEnd.toISOString(),
        geofenceRadius,
        instructions,
      });

      onClose();
      if (onConfirmed) {
        onConfirmed();
      } else {
        // Refresh dashboard
        navigate(0);
      }
    } catch (err: any) {
      console.error('Confirmation error:', err);
      setError(err?.response?.data?.error || 'Failed to confirm pickup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Confirm Pickup Details"
      onCancel={onClose}
      onConfirm={handleConfirm}
      confirmLabel={loading ? 'Confirming...' : 'Confirm Pickup'}
      cancelLabel="Cancel"
      confirmVariant="primary"
    >
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="bg-[var(--bg-tint)] rounded-lg p-4">
          <p className="text-sm text-[var(--text-secondary)] mb-1">Renter</p>
          <p className="font-semibold text-[var(--text-primary)]">{renterName}</p>
          <p className="text-sm text-[var(--text-secondary)] mt-2 mb-1">Item</p>
          <p className="font-semibold text-[var(--text-primary)]">{itemTitle}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            Pickup Location
          </label>
          <div className="flex items-center gap-2 text-[var(--text-secondary)]">
            <i className="mdi mdi-map-marker text-[var(--forest-green)]"></i>
            <span>{pickupAddress}</span>
          </div>
          {rentalId && (
            <p className="text-xs text-[var(--text-muted)] mt-2">
              Need to switch this to shipping or DoorDash instead?{' '}
              <Link to={`/rental/${rentalId}`} onClick={onClose} className="text-[var(--forest-green)] font-medium hover:underline">
                Change fulfillment type
              </Link>
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            Pickup Date
          </label>
          <input
            type="date"
            value={pickupDate}
            onChange={(e) => setPickupDate(e.target.value)}
            className="w-full px-3 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              Start Time
            </label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              End Time
            </label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            Geofence Radius: {geofenceRadius}m
          </label>
          <input
            type="range"
            min="50"
            max="500"
            step="50"
            value={geofenceRadius}
            onChange={(e) => setGeofenceRadius(Number(e.target.value))}
            className="w-full"
          />
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Renter must be within this distance to check in
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            Pickup Instructions (optional)
          </label>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="e.g., Ring doorbell, items are in the garage"
            rows={3}
            className="w-full px-3 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)] resize-none"
          />
        </div>
      </div>
    </Modal>
  );
}
